import { c as _c } from "react/compiler-runtime";
import { cn } from '@core/utils';
import { NodeSelection } from 'prosemirror-state';
import type { ReactNode } from 'react';
import { useContext, useState } from 'react';
import { Button } from '../../atom/Button/Button';
import { Input } from '../../atom/Input/Input';
import { InputDecorator } from '../../atom/InputDecorator/InputDecorator';
import { Modal } from '../../atom/Modal/Modal';
import { Radio } from '../../atom/Radio/Radio';
import { CustomEditorContext } from '.';
import BoxedTextIcon from './_assets/boxed-text.svg';
import HrIcon from './_assets/hr.svg';
import type { ImageButtonProps } from './_components/ImageButton';
import { ImageButton } from './_components/ImageButton';
import { TEXT_ALIGN } from './_constants';
import { CLASS_TO_BUTTON_SIZE, CLASS_TO_BUTTON_THEME, type CustomLinkOptions } from './_utils/CustomLink';
import { FONT_SIZES, type FontSizeType } from './_utils/FontSize';
import { FONT_WEIGHT_LABELS, FONT_WEIGHTS } from './_utils/FontWeight';
import { HIGHLIGHT_LABELS, HIGHLIGHTS } from './_utils/Highlight';
import { TEXT_COLOR_LABELS, TEXT_COLORS } from './_utils/TextColor';
export interface ToolbarProps {
  imageUploadOptions?: ImageButtonProps;
  children?: ReactNode;
}
type Level = 2 | 3 | 4 | 5 | 6;
const HEADING_OPTIONS = [{
  value: 'paragraph',
  label: '본문'
}, {
  value: 'h2',
  label: '제목 2'
}, {
  value: 'h3',
  label: '제목 3'
}, {
  value: 'h4',
  label: '제목 4'
}, {
  value: 'h5',
  label: '제목 5'
}, {
  value: 'h6',
  label: '제목 6'
}];
export const Toolbar = t0 => {
  const $ = _c(128);
  const {
    imageUploadOptions,
    children
  } = t0;
  const {
    editor
  } = useContext(CustomEditorContext);
  const [isLinkModalOpened, setIsLinkModalOpened] = useState(false);
  const isImageSelected = editor?.state.selection instanceof NodeSelection && editor?.state.selection.node.type.name === "image";
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      theme: "text",
      size: "H56",
      isOutLink: false,
      href: ""
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [customLinkOptions, setCustomLinkOptions] = useState(t1);
  let t2;
  if ($[1] !== editor || $[2] !== isImageSelected) {
    t2 = () => {
      if (isImageSelected) {
        const imageAttrs = editor?.state.selection instanceof NodeSelection ? editor.state.selection.node.attrs : null;
        setCustomLinkOptions(prev => ({
          ...prev,
          theme: "image",
          href: imageAttrs?.href || "",
          isOutLink: imageAttrs?.target === "_blank"
        }));
      } else {
        if (editor?.isActive("link")) {
          const linkAttrs = editor.getAttributes("link");
          const linkClass = linkAttrs.class || "";
          const theme = Object.entries(CLASS_TO_BUTTON_THEME).find(t3 => {
            const [cls] = t3;
            return linkClass.includes(cls);
          })?.[1] as CustomLinkOptions['theme'] ?? "text";
          const size = Object.entries(CLASS_TO_BUTTON_SIZE).find(t4 => {
            const [cls_0] = t4;
            return linkClass.includes(cls_0);
          })?.[1] as CustomLinkOptions['size'] ?? "H56";
          setCustomLinkOptions({
            theme,
            size,
            href: linkAttrs.href || "",
            isOutLink: linkAttrs.target === "_blank"
          });
        } else {
          setCustomLinkOptions({
            theme: "text",
            size: "H56",
            href: "",
            isOutLink: false
          });
        }
      }
      setIsLinkModalOpened(true);
    };
    $[1] = editor;
    $[2] = isImageSelected;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleLinkButtonClick = t2;
  if (!editor) {
    return null;
  }
  let t3;
  if ($[4] !== editor) {
    t3 = () => {
      for (let i = 2; i <= 6; i++) {
        if (editor.isActive("heading", {
          level: i
        })) {
          return `h${i}`;
        }
      }
      return "paragraph";
    };
    $[4] = editor;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const getSelectedHeading = t3;
  const selectedHeading = getSelectedHeading();
  let t4;
  if ($[6] !== editor) {
    t4 = TEXT_COLORS.find(t5 => {
      const [, color] = t5;
      return editor.isActive("textStyle", {
        color
      });
    }) ?? [];
    $[6] = editor;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const [selectedColor] = t4;
  let t5;
  if ($[8] !== editor) {
    t5 = FONT_WEIGHTS.find(fontWeight => editor.isActive("fontWeight", {
      fontWeight
    }));
    $[8] = editor;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const selectedFontWeight = t5;
  let t6;
  if ($[10] !== editor) {
    t6 = FONT_SIZES.find(fontSize => editor.isActive("fontSize", {
      fontSize
    }));
    $[10] = editor;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const selectedFontSize = t6;
  let isBoxedText;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t7;
  let t8;
  let t9;
  if ($[12] !== editor || $[13] !== selectedColor || $[14] !== selectedFontSize || $[15] !== selectedFontWeight || $[16] !== selectedHeading) {
    isBoxedText = editor.isActive("boxedText");
    let t18;
    if ($[29] !== editor) {
      t18 = TEXT_ALIGN.find(textAlign => editor.isActive("textAlign", {
        textAlign
      }));
      $[29] = editor;
      $[30] = t18;
    } else {
      t18 = $[30];
    }
    const selectedTextAlign = t18;
    t10 = "flex flex-wrap items-center justify-center gap-8 bg-Gray-100 p-8";
    if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <p className="text-Gray-800 typo-caption1">*h1 태그는 제목 고정입니다.</p>;
      $[31] = t11;
    } else {
      t11 = $[31];
    }
    let t19;
    if ($[32] !== editor) {
      t19 = e => {
        const value = e.target.value;
        if (value === "paragraph") {
          editor.chain().focus().setParagraph().run();
        } else {
          const level = Number.parseInt(value.substring(1)) as Level;
          editor.chain().focus().setHeading({
            level
          }).run();
        }
      };
      $[32] = editor;
      $[33] = t19;
    } else {
      t19 = $[33];
    }
    let t20;
    if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
      t20 = HEADING_OPTIONS.map(_temp);
      $[34] = t20;
    } else {
      t20 = $[34];
    }
    if ($[35] !== selectedHeading || $[36] !== t19) {
      t12 = <select aria-label={"\uC81C\uBAA9 \uC2A4\uD0C0\uC77C"} value={selectedHeading} onChange={t19}>{t20}</select>;
      $[35] = selectedHeading;
      $[36] = t19;
      $[37] = t12;
    } else {
      t12 = $[37];
    }
    let t21;
    if ($[38] !== editor) {
      t21 = e_0 => {
        editor.chain().focus().setFontSize(e_0.target.value as FontSizeType).run();
      };
      $[38] = editor;
      $[39] = t21;
    } else {
      t21 = $[39];
    }
    let t22;
    if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
      t22 = FONT_SIZES.map(_temp2);
      $[40] = t22;
    } else {
      t22 = $[40];
    }
    if ($[41] !== selectedFontSize || $[42] !== t21) {
      t13 = <select aria-label={"\uAE00\uC790 \uD06C\uAE30"} value={selectedFontSize} onChange={t21}>{t22}</select>;
      $[41] = selectedFontSize;
      $[42] = t21;
      $[43] = t13;
    } else {
      t13 = $[43];
    }
    let t23;
    if ($[44] !== editor) {
      t23 = e_1 => {
        editor?.chain().focus().setTextAlign(e_1.target.value).run();
      };
      $[44] = editor;
      $[45] = t23;
    } else {
      t23 = $[45];
    }
    let t24;
    if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
      t24 = TEXT_ALIGN.map(_temp3);
      $[46] = t24;
    } else {
      t24 = $[46];
    }
    if ($[47] !== selectedTextAlign || $[48] !== t23) {
      t14 = <select aria-label={"\uC815\uB82C"} value={selectedTextAlign} onChange={t23}>{t24}</select>;
      $[47] = selectedTextAlign;
      $[48] = t23;
      $[49] = t14;
    } else {
      t14 = $[49];
    }
    let t25;
    if ($[50] !== editor || $[51] !== selectedColor) {
      t25 = TEXT_COLORS.map(t26 => {
        const [label, color_0] = t26;
        return <button key={label} aria-label={`글자색 ${TEXT_COLOR_LABELS[label]}`} aria-pressed={selectedColor === label} className={cn("font-800", {
          underline: selectedColor === label
        })} style={{
          color: color_0
        }} type="button" onClick={() => editor?.chain().focus().setColor(color_0).run()}>A</button>;
      });
      $[50] = editor;
      $[51] = selectedColor;
      $[52] = t25;
    } else {
      t25 = $[52];
    }
    if ($[53] !== t25) {
      t15 = <section className="flex gap-8 rounded-xsmall border-1 border-Gray-600 px-8 py-2">{t25}</section>;
      $[53] = t25;
      $[54] = t15;
    } else {
      t15 = $[54];
    }
    let t26;
    if ($[55] !== editor || $[56] !== selectedFontWeight) {
      t26 = FONT_WEIGHTS.map(fontWeight_0 => <button key={fontWeight_0} aria-label={FONT_WEIGHT_LABELS[fontWeight_0]} aria-pressed={selectedFontWeight === fontWeight_0} className={cn(fontWeight_0, {
        underline: selectedFontWeight === fontWeight_0
      })} type="button" onClick={() => editor?.chain().focus().setFontWeight(fontWeight_0).run()}>B</button>);
      $[55] = editor;
      $[56] = selectedFontWeight;
      $[57] = t26;
    } else {
      t26 = $[57];
    }
    if ($[58] !== t26) {
      t16 = <section className="flex gap-8 rounded-xsmall border-1 border-Gray-600 px-8 py-2">{t26}</section>;
      $[58] = t26;
      $[59] = t16;
    } else {
      t16 = $[59];
    }
    if ($[60] !== editor) {
      t17 = HIGHLIGHTS.map(highlight => {
        const isActive = editor.isActive("highlight", {
          highlight
        });
        return <button key={highlight} aria-label={HIGHLIGHT_LABELS[highlight]} aria-pressed={isActive} className={cn(highlight, "flex size-24 min-h-24 min-w-24 items-center justify-center rounded-xsmall font-700", {
          "border-1 border-Gray-800": isActive
        })} type="button" onClick={() => isActive ? editor.chain().focus().unsetHighlight().run() : editor.chain().focus().setHighlight(highlight).run()} />;
      });
      $[60] = editor;
      $[61] = t17;
    } else {
      t17 = $[61];
    }
    t7 = "\uBC15\uC2A4\uD14D\uC2A4\uD2B8";
    t8 = isBoxedText;
    t9 = cn("box-border flex size-24 items-center justify-center", {
      "border-Gray-800 border-1 rounded-xxsmall": isBoxedText
    });
    $[12] = editor;
    $[13] = selectedColor;
    $[14] = selectedFontSize;
    $[15] = selectedFontWeight;
    $[16] = selectedHeading;
    $[17] = isBoxedText;
    $[18] = t10;
    $[19] = t11;
    $[20] = t12;
    $[21] = t13;
    $[22] = t14;
    $[23] = t15;
    $[24] = t16;
    $[25] = t17;
    $[26] = t7;
    $[27] = t8;
    $[28] = t9;
  } else {
    isBoxedText = $[17];
    t10 = $[18];
    t11 = $[19];
    t12 = $[20];
    t13 = $[21];
    t14 = $[22];
    t15 = $[23];
    t16 = $[24];
    t17 = $[25];
    t7 = $[26];
    t8 = $[27];
    t9 = $[28];
  }
  let t18;
  if ($[62] !== editor || $[63] !== isBoxedText) {
    t18 = () => isBoxedText ? editor.chain().focus().unsetBoxedText().run() : editor.chain().focus().setBoxedText().run();
    $[62] = editor;
    $[63] = isBoxedText;
    $[64] = t18;
  } else {
    t18 = $[64];
  }
  let t19;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <BoxedTextIcon />;
    $[65] = t19;
  } else {
    t19 = $[65];
  }
  let t20;
  if ($[66] !== t18 || $[67] !== t7 || $[68] !== t8 || $[69] !== t9) {
    t20 = <button aria-label={t7} aria-pressed={t8} className={t9} type="button" onClick={t18}>{t19}</button>;
    $[66] = t18;
    $[67] = t7;
    $[68] = t8;
    $[69] = t9;
    $[70] = t20;
  } else {
    t20 = $[70];
  }
  let t21;
  if ($[71] !== editor) {
    t21 = () => {
      editor?.chain().focus().setHorizontalRule().run();
    };
    $[71] = editor;
    $[72] = t21;
  } else {
    t21 = $[72];
  }
  let t22;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <HrIcon />;
    $[73] = t22;
  } else {
    t22 = $[73];
  }
  let t23;
  if ($[74] !== t21) {
    t23 = <button aria-label={"\uAD6C\uBD84\uC120 \uC0BD\uC785"} type="button" onClick={t21}>{t22}</button>;
    $[74] = t21;
    $[75] = t23;
  } else {
    t23 = $[75];
  }
  let t24;
  if ($[76] !== imageUploadOptions) {
    t24 = <ImageButton {...imageUploadOptions} />;
    $[76] = imageUploadOptions;
    $[77] = t24;
  } else {
    t24 = $[77];
  }
  let t25;
  if ($[78] !== handleLinkButtonClick) {
    t25 = <Button dimensions="H26" onClick={handleLinkButtonClick}>링크</Button>;
    $[78] = handleLinkButtonClick;
    $[79] = t25;
  } else {
    t25 = $[79];
  }
  let t26;
  if ($[80] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = () => setIsLinkModalOpened(false);
    $[80] = t26;
  } else {
    t26 = $[80];
  }
  let t27;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = e_2 => {
      setCustomLinkOptions(prev_0 => ({
        ...prev_0,
        href: e_2.target.value
      }));
    };
    $[81] = t27;
  } else {
    t27 = $[81];
  }
  let t28;
  if ($[82] !== customLinkOptions.href) {
    t28 = <InputDecorator label={"\uC8FC\uC18C"}><Input value={customLinkOptions.href} onChange={t27} /></InputDecorator>;
    $[82] = customLinkOptions.href;
    $[83] = t28;
  } else {
    t28 = $[83];
  }
  let t29;
  let t30;
  let t31;
  let t32;
  let t33;
  if ($[84] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = value_0 => {
      setCustomLinkOptions(prev_1 => ({
        ...prev_1,
        theme: value_0
      }));
    };
    t30 = <Radio.Item value="text">문자</Radio.Item>;
    t31 = <Radio.Item value="primary-fill-button">버튼 1</Radio.Item>;
    t32 = <Radio.Item value="sub-fill-button">버튼 2</Radio.Item>;
    t33 = <Radio.Item value="image">이미지</Radio.Item>;
    $[84] = t29;
    $[85] = t30;
    $[86] = t31;
    $[87] = t32;
    $[88] = t33;
  } else {
    t29 = $[84];
    t30 = $[85];
    t31 = $[86];
    t32 = $[87];
    t33 = $[88];
  }
  let t34;
  if ($[89] !== customLinkOptions.theme) {
    t34 = <InputDecorator label={"\uB9C1\uD06C \uD615\uC2DD"}><Radio.Group value={customLinkOptions.theme} onChange={t29}>{t30}{t31}{t32}{t33}</Radio.Group></InputDecorator>;
    $[89] = customLinkOptions.theme;
    $[90] = t34;
  } else {
    t34 = $[90];
  }
  const t35 = customLinkOptions.isOutLink ? "out" : "in";
  let t36;
  let t37;
  let t38;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = value_1 => {
      setCustomLinkOptions(prev_2 => ({
        ...prev_2,
        isOutLink: value_1 === "out"
      }));
    };
    t37 = <Radio.Item value="out">외부에서 열기</Radio.Item>;
    t38 = <Radio.Item value="in">내부에서 열기</Radio.Item>;
    $[91] = t36;
    $[92] = t37;
    $[93] = t38;
  } else {
    t36 = $[91];
    t37 = $[92];
    t38 = $[93];
  }
  let t39;
  if ($[94] !== t35) {
    t39 = <InputDecorator label={"\uCC3D \uC5F4\uAE30"}><Radio.Group value={t35} onChange={t36}>{t37}{t38}</Radio.Group></InputDecorator>;
    $[94] = t35;
    $[95] = t39;
  } else {
    t39 = $[95];
  }
  let t40;
  if ($[96] !== t28 || $[97] !== t34 || $[98] !== t39) {
    t40 = <article className="mb-32 flex flex-col gap-16">{t28}{t34}{t39}</article>;
    $[96] = t28;
    $[97] = t34;
    $[98] = t39;
    $[99] = t40;
  } else {
    t40 = $[99];
  }
  const t41 = !customLinkOptions.href;
  let t42;
  if ($[100] !== customLinkOptions || $[101] !== editor) {
    t42 = () => {
      setIsLinkModalOpened(false);
      editor.chain().focus().setCustomLink(customLinkOptions).run();
    };
    $[100] = customLinkOptions;
    $[101] = editor;
    $[102] = t42;
  } else {
    t42 = $[102];
  }
  let t43;
  if ($[103] !== t41 || $[104] !== t42) {
    t43 = <Button className="flex-1" disabled={t41} onClick={t42}>설정</Button>;
    $[103] = t41;
    $[104] = t42;
    $[105] = t43;
  } else {
    t43 = $[105];
  }
  let t44;
  if ($[106] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <Button className="flex-1" theme="sub-fill" onClick={() => {
      setIsLinkModalOpened(false);
    }}>취소</Button>;
    $[106] = t44;
  } else {
    t44 = $[106];
  }
  let t45;
  if ($[107] !== t43) {
    t45 = <footer className="flex w-full gap-8">{t43}{t44}</footer>;
    $[107] = t43;
    $[108] = t45;
  } else {
    t45 = $[108];
  }
  let t46;
  if ($[109] !== isLinkModalOpened || $[110] !== t40 || $[111] !== t45) {
    t46 = <Modal opened={isLinkModalOpened} onClose={t26}>{t40}{t45}</Modal>;
    $[109] = isLinkModalOpened;
    $[110] = t40;
    $[111] = t45;
    $[112] = t46;
  } else {
    t46 = $[112];
  }
  let t47;
  if ($[113] !== children || $[114] !== t10 || $[115] !== t11 || $[116] !== t12 || $[117] !== t13 || $[118] !== t14 || $[119] !== t15 || $[120] !== t16 || $[121] !== t17 || $[122] !== t20 || $[123] !== t23 || $[124] !== t24 || $[125] !== t25 || $[126] !== t46) {
    t47 = <nav className={t10}>{t11}{t12}{t13}{t14}{t15}{t16}{t17}{t20}{t23}{t24}{t25}{t46}{children}</nav>;
    $[113] = children;
    $[114] = t10;
    $[115] = t11;
    $[116] = t12;
    $[117] = t13;
    $[118] = t14;
    $[119] = t15;
    $[120] = t16;
    $[121] = t17;
    $[122] = t20;
    $[123] = t23;
    $[124] = t24;
    $[125] = t25;
    $[126] = t46;
    $[127] = t47;
  } else {
    t47 = $[127];
  }
  return t47;
};
function _temp(option) {
  return <option key={option.value} value={option.value}>{option.label}</option>;
}
function _temp2(fontSize_0) {
  return <option key={fontSize_0} className={fontSize_0} value={fontSize_0}>{fontSize_0}</option>;
}
function _temp3(textAlign_0) {
  return <option key={textAlign_0} value={textAlign_0}>{textAlign_0}</option>;
}